import Base from '../base/base';
import gsap from 'gsap';
import './hero-triangle-overlay.scss';

type PositionValue = number | string;

interface TriangleConfig {
  x: PositionValue;
  y: PositionValue;
}

interface HeroTriangleOverlayOptions {
  selector?: string;
  delay?: number;
  stagger?: number;
  mobileTriggerViewportRatio?: number;
  debug?: boolean;
  triangles?: TriangleConfig[];
}

const DEFAULT_TRIANGLES: TriangleConfig[] = [
  // { x:0, y: 0 },
  { x: "-105px", y: "70%" },
  { x: "45px", y: "74%" },
  { x: "145px", y: "38%" },
  { x: "-135px", y: "20%" },
  { x: "50px", y: "12%" },
  { x: "125px", y: "-9%" },
  { x: "-15px", y: "-42%" },
  { x: "-40%", y: "-75%" },
];

export default class HeroTriangleOverlay extends Base {
  private mobileBreakpoint: number = 768;
  private mobileTriggerViewportRatio: number;
  private selector: string;
  private delay: number;
  private stagger: number;
  private triangles: TriangleConfig[];
  private container: HTMLElement | null = null;
  private triangleEls: HTMLElement[] = [];
  private triangleShapeEls: HTMLElement[] = [];
  private hasAnimated: boolean = false;
  private resizeTarget: HTMLElement | null = null;
  private resizeObserver: ResizeObserver | null = null;
  private imageEl: HTMLImageElement | null = null;
  private pendingResizeTimer: number | null = null;
  private resizeDebounceMs: number = 200;
  private lastOverlayWidth: number | null = null;
  private lastOverlayHeight: number | null = null;
  private lastOverlayLeft: number | null = null;
  private lastOverlayTop: number | null = null;

  constructor(options: HeroTriangleOverlayOptions = {}) {
    super(options.debug || false);
    this.selector = options.selector || '.hp-hero .hero-triangle-overlay';
    this.delay = this.isMobileViewport()
      ? 0
      : (options.delay ?? 0.4);
    this.stagger = options.stagger ?? 0.2;
    this.mobileTriggerViewportRatio = options.mobileTriggerViewportRatio ?? 0.8;
    this.triangles = options.triangles ?? DEFAULT_TRIANGLES;

    this.init();
  }

  init(): void {
    this.container = document.querySelector(this.selector);
    if (!this.container) {
      this.log('HeroTriangleOverlay: container not found');
      return;
    }

    this.renderTriangles();
    this.setupResizeObserver();
    this.setupObserver();
  }

  private renderTriangles(): void {
    if (!this.container) return;

    this.container.innerHTML = '';
    this.triangleEls = [];
    this.triangleShapeEls = [];
    this.triangles.forEach((triangle) => {
      const xValue = this.formatPosition(triangle.x);
      const yValue = this.formatPosition(triangle.y);
      const wrapper = document.createElement('div');
      const shape = document.createElement('div');
      wrapper.classList.add('hero-triangle');
      shape.classList.add('hero-triangle__shape');
      wrapper.style.setProperty('--x', xValue);
      wrapper.style.setProperty('--y', yValue);
      wrapper.appendChild(shape);
      this.container?.appendChild(wrapper);
      this.triangleEls.push(wrapper);
      this.triangleShapeEls.push(shape);
    });
  }

  private updateLayout(): void {
    if (!this.container || !this.triangleEls.length || !this.resizeTarget) return;

    const bounds = this.computeImageBounds();
    if (!bounds) return;

    const { width, height, left, top } = bounds;
    if (
      this.lastOverlayWidth === width &&
      this.lastOverlayHeight === height &&
      this.lastOverlayLeft === left &&
      this.lastOverlayTop === top
    ) {
      return;
    }

    this.lastOverlayWidth = width;
    this.lastOverlayHeight = height;
    this.lastOverlayLeft = left;
    this.lastOverlayTop = top;

    this.container.style.inset = 'auto';
    this.container.style.left = `${left}px`;
    this.container.style.top = `${top}px`;
    this.container.style.width = `${width}px`;
    this.container.style.height = `${height}px`;

    this.triangles.forEach((triangle, index) => {
      const el = this.triangleEls[index];
      if (!el) return;
      const xValue = this.formatPosition(triangle.x);
      const yValue = this.formatPosition(triangle.y);
      el.style.setProperty('--x', xValue);
      el.style.setProperty('--y', yValue);
      this.log('HeroTriangleOverlay: triangle position', {
        index,
        x: xValue,
        y: yValue
      });
    });
  }

  private queueResizeUpdate(): void {
    if (this.pendingResizeTimer !== null) {
      window.clearTimeout(this.pendingResizeTimer);
    }

    this.pendingResizeTimer = window.setTimeout(() => {
      this.pendingResizeTimer = null;
      this.updateLayout();
    }, this.resizeDebounceMs);
  }

  private setupResizeObserver(): void {
    if (!this.container) return;

    this.resizeTarget = this.container.closest('.twocol__img') || this.container;
    this.imageEl = this.resizeTarget.querySelector('img');

    if (this.imageEl) {
      this.imageEl.addEventListener('load', () => this.queueResizeUpdate());
    }

    const ResizeObserverCtor = (
      window as Window & { ResizeObserver?: typeof ResizeObserver }
    ).ResizeObserver;

    if (typeof ResizeObserverCtor === 'function') {
      this.resizeObserver = new ResizeObserverCtor(() => {
        this.queueResizeUpdate();
      });
      this.resizeObserver.observe(this.resizeTarget);
    } else {
      window.addEventListener('resize', () => this.queueResizeUpdate(), { passive: true });
    }

    this.queueResizeUpdate();
  }

  private computeImageBounds(): {
    width: number;
    height: number;
    left: number;
    top: number;
  } | null {
    if (!this.resizeTarget) return null;

    const containerRect = this.resizeTarget.getBoundingClientRect();
    const containerWidth = containerRect.width;
    const containerHeight = containerRect.height;
    if (containerWidth === 0 || containerHeight === 0) return null;

    if (!this.imageEl) {
      return {
        width: containerWidth,
        height: containerHeight,
        left: 0,
        top: 0
      };
    }

    const naturalWidth = this.imageEl.naturalWidth || containerWidth;
    const naturalHeight = this.imageEl.naturalHeight || containerHeight;
    const styles = window.getComputedStyle(this.imageEl);
    const objectFit = styles.objectFit || 'fill';
    const { x: posX, y: posY } = this.parseObjectPosition(styles.objectPosition);

    let displayWidth = containerWidth;
    let displayHeight = containerHeight;

    if (objectFit === 'cover' || objectFit === 'contain') {
      const scale = objectFit === 'cover'
        ? Math.max(containerWidth / naturalWidth, containerHeight / naturalHeight)
        : Math.min(containerWidth / naturalWidth, containerHeight / naturalHeight);
      displayWidth = naturalWidth * scale;
      displayHeight = naturalHeight * scale;
    } else if (objectFit === 'none') {
      displayWidth = naturalWidth;
      displayHeight = naturalHeight;
    }

    const left = (containerWidth - displayWidth) * posX;
    const top = (containerHeight - displayHeight) * posY;

    return {
      width: displayWidth,
      height: displayHeight,
      left,
      top
    };
  }

  private parseObjectPosition(value: string): { x: number; y: number } {
    const fallback = { x: 0.5, y: 0.5 };
    if (!value) return fallback;

    const parts = value.trim().split(/\s+/);
    const xPart = parts[0] || '50%';
    const yPart = parts[1] || '50%';

    const parsePart = (part: string, axis: 'x' | 'y'): number => {
      const keywordMap: Record<string, number> = {
        left: 0,
        center: 0.5,
        right: 1,
        top: 0,
        bottom: 1
      };

      if (keywordMap[part] !== undefined) {
        const value = keywordMap[part];
        if (axis === 'x' && (part === 'top' || part === 'bottom')) return 0.5;
        if (axis === 'y' && (part === 'left' || part === 'right')) return 0.5;
        return value;
      }

      const percentMatch = part.match(/^(-?\d*\.?\d+)%$/);
      if (percentMatch) {
        return parseFloat(percentMatch[1]) / 100;
      }

      return 0.5;
    };

    return {
      x: parsePart(xPart, 'x'),
      y: parsePart(yPart, 'y')
    };
  }

  private formatPosition(value: PositionValue): string {
    if (typeof value === 'number') {
      return `${value * 0.5}%`;
    }

    const trimmed = value.trim();
    const pxMatch = trimmed.match(/^(-?\d*\.?\d+)px$/i);
    if (pxMatch) {
      return `${parseFloat(pxMatch[1])}px`;
    }

    const percentMatch = trimmed.match(/^(-?\d*\.?\d+)%$/);
    if (percentMatch) {
      return `${parseFloat(percentMatch[1]) * 0.5}%`;
    }

    const numeric = parseFloat(trimmed);
    if (Number.isFinite(numeric)) {
      return `${numeric * 0.5}%`;
    }

    return '0%';
  }

  private isMobileViewport(): boolean {
    return window.innerWidth <= this.mobileBreakpoint;
  }

  private animateTriangles(): void {
    if (!this.triangleEls.length || this.hasAnimated) return;
    this.hasAnimated = true;
    this.log('HeroTriangleOverlay: animation start');

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
      this.triangleShapeEls.forEach((triangle) => {
        gsap.set(triangle, { autoAlpha: 1, y: 0 });
      });
      return;
    }

    gsap.set(this.triangleShapeEls, { autoAlpha: 0, y: -10, scale: 2 });
    gsap.to(this.triangleShapeEls, {
      autoAlpha: 1,
      y: 0,
      duration: 0.6,
      scale: 0.9,
      ease: 'power2.out',
      delay: this.delay,
      stagger: this.stagger,
      onComplete: () => this.startHoverAnimation()
    });
  }

  private startHoverAnimation(): void {
    if (!this.triangleShapeEls.length) return;

    gsap.to(this.triangleShapeEls, {
      y: -4,
      duration: 1,
      ease: 'sine.inOut',
      yoyo: true,
      repeat: -1,
      stagger: 0
    });
  }

  private setupObserver(): void {
    if (!this.container) return;

    const target = this.container.closest('.twocol__img') || this.container;

    if (!('IntersectionObserver' in window)) {
      this.log('HeroTriangleOverlay: IntersectionObserver not supported, animating immediately');
      this.animateTriangles();
      return;
    }

    const observer = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          const rootBottom = entry.rootBounds?.bottom ?? window.innerHeight;
          const targetBottom = entry.boundingClientRect.bottom;
          const isBottomInView = targetBottom <= rootBottom;
          const rootTop = entry.rootBounds?.top ?? 0;
          const rootHeight = entry.rootBounds?.height ?? window.innerHeight;
          const viewportMid = rootTop + (rootHeight * this.mobileTriggerViewportRatio);
          const targetTop = entry.boundingClientRect.top;
          const isTopPastMid = targetTop <= viewportMid;
          const shouldAnimate = this.isMobileViewport()
            ? (entry.isIntersecting && isTopPastMid)
            : (entry.isIntersecting && isBottomInView);

          this.log('HeroTriangleOverlay: intersection', {
            isIntersecting: entry.isIntersecting,
            targetBottom,
            rootBottom,
            isBottomInView,
            targetTop,
            viewportMid,
            isTopPastMid
          });

          if (shouldAnimate) {
            this.log('HeroTriangleOverlay: trigger met, animating');
            this.animateTriangles();
            obs.disconnect();
          }
        });
      },
      { threshold: [0, 1] }
    );

    observer.observe(target);
    this.log('HeroTriangleOverlay: observer attached');
  }
}
